import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { FromNow } from '@plato/app-shared/components/from-now';
import { MaskedContent } from '@plato/app-shared/components/masked-content';
import {
    MetricHintExplanation,
    useMetricHintOpenedTracking,
} from '@plato/app-shared/components/metric-explainer';
import { DateText } from '@plato/ui-legacy/components/common/date-time-text';
import { DoubleDeckerCell } from '@plato/ui-legacy/components/list-views/double-decker-cell';
import { IdAndNameCell } from '@plato/ui-legacy/components/list-views/id-and-name-cell';
import { MarginPercentage } from '@plato/ui-legacy/components/list-views/margin-percentage';
import { DataText } from '@plato/ui-legacy/components/typography/data-text';
import type { ColumnDef } from '@plato/ui/components/data-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { GetManyQuotesOrdersListItem } from '../../../server/types/infer-trpc-types.ts';
import { QuotesOrdersSortBy } from '../../../server/types/list-view-types.ts';
import { safeJoin } from '../../../utils/string-utils.ts';
import { OrderLink } from '../../quote-order-detail/components/quote-order-link.tsx';
import { OrderStatusBadge } from '../../statuses/components/order-status-badge.tsx';
import type { QuoteOrderSummaryData } from './use-quote-columns-for-list-grid.tsx';

type OrderColumnDef = ColumnDef<GetManyQuotesOrdersListItem, QuoteOrderSummaryData, QuotesOrdersSortBy>;

export function useOrderColumnsForListGrid() {
    const { t } = useTranslation();
    const trackMetricHintOpened = useMetricHintOpenedTracking();
    const { canReadGrossMargin, canReadClerkAssignment, canReadPONumber } = useAppConfig();

    const orderColumns: OrderColumnDef[] = useMemo(
        () => [
            {
                accessorKey: 'id',
                header: t('table_header__order_id', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => <OrderLink transactionId={row.original.sales_order_id} />,
                size: 110,
                meta: {
                    pinned: 'left',
                    summary: () => t('table_footer__sum', { ns: 'quotes-orders-list' }),
                },
            },
            {
                accessorKey: 'customer_name',
                meta: {
                    aiChat: { label: t('table_header__customer', { ns: 'quotes-orders-list' }) },
                    sortBy: QuotesOrdersSortBy.CUSTOMER_NAME,
                },
                header: t('table_header__customer', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ cell }) => (
                    <IdAndNameCell
                        to={`/customers/${encodeURIComponent(cell.row.original.customer_id)}`}
                        id={cell.row.original.customer_id}
                        title={cell.renderValue() as string}
                    />
                ),
                size: 250,
            },
            {
                accessorKey: 'entry_date',
                meta: {
                    aiChat: { label: t('table_header__order_created', { ns: 'quotes-orders-list' }) },
                    sortBy: QuotesOrdersSortBy.ORDER_CREATED_AT_DATE,
                },
                header: t('table_header__order_created', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <DoubleDeckerCell
                        align="left"
                        topClassName="text-neutral-500"
                        topLevel={<FromNow>{row.original.entry_date}</FromNow>}
                        bottomLevel={<DateText date={row.original.entry_date} />}
                    />
                ),
            },
            {
                accessorKey: 'total_amount_net',
                meta: {
                    sortBy: QuotesOrdersSortBy.LINE_TOTAL,
                    aiChat: {
                        label: t('table_header__total', { ns: 'quotes-orders-list' }),
                        description: 'Net order total (excluding tax), in the tenant’s base currency.',
                    },
                    summary: (data) => (
                        <DataText>
                            <CurrencyOrEmpty decimals={0}>{data.total_amount_net}</CurrencyOrEmpty>
                        </DataText>
                    ),
                },
                header: t('table_header__total', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <DataText>
                        <CurrencyOrEmpty decimals={2}>{row.original.total_amount_net}</CurrencyOrEmpty>
                    </DataText>
                ),
            },
            {
                accessorKey: 'total_margin',
                meta: {
                    sortBy: QuotesOrdersSortBy.GROSS_MARGIN,
                    enabled: canReadGrossMargin,
                    aiChat: {
                        label: t('table_header__margin', { ns: 'quotes-orders-list' }),
                        requires: ['canReadGrossMargin'],
                    },
                    summary: (data) => (
                        <MaskedContent>
                            <DataText>
                                <CurrencyOrEmpty decimals={0}>{data.total_margin}</CurrencyOrEmpty>
                            </DataText>
                        </MaskedContent>
                    ),
                    hint: <MetricHintExplanation base="ORDER_VALUE" metric="GROSS_MARGIN" />,
                    onHintOpen: () => {
                        trackMetricHintOpened('GROSS_MARGIN');
                    },
                },
                header: () => (
                    <MaskedContent>{t('table_header__margin', { ns: 'quotes-orders-list' })}</MaskedContent>
                ),
                cell: ({ row }) => (
                    <MaskedContent>
                        <DataText>
                            <CurrencyOrEmpty decimals={2}>{row.original.total_margin}</CurrencyOrEmpty>
                        </DataText>
                    </MaskedContent>
                ),
            },
            {
                accessorKey: 'total_margin_percent',
                meta: {
                    enabled: canReadGrossMargin,
                    aiChat: {
                        label: t('table_header__margin_percent', { ns: 'quotes-orders-list' }),
                        description: 'Gross margin as a percentage of the net total',
                        requires: ['canReadGrossMargin'],
                    },
                    summary: (data) => (
                        <MaskedContent>
                            <DataText>
                                <MarginPercentage
                                    margin={data.total_margin}
                                    revenue={data.total_amount_net}
                                />
                            </DataText>
                        </MaskedContent>
                    ),
                    hint: <MetricHintExplanation base="ORDER_VALUE" metric="GROSS_MARGIN_PERCENTAGE" />,
                    onHintOpen: () => {
                        trackMetricHintOpened('GROSS_MARGIN_PERCENTAGE');
                    },
                },
                header: () => (
                    <MaskedContent>
                        {t('table_header__margin_percent', { ns: 'quotes-orders-list' })}
                    </MaskedContent>
                ),
                cell: ({ row }) => (
                    <MaskedContent>
                        <DataText>
                            <MarginPercentage
                                margin={row.original.total_margin}
                                revenue={row.original.total_amount_net}
                            />
                        </DataText>
                    </MaskedContent>
                ),
            },
            {
                accessorKey: 'document_type',
                header: t('table_header__order_type', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) =>
                    row.original.document_type === row.original.document_type_name
                        ? row.original.document_type
                        : `${row.original.document_type}: ${row.original.document_type_name}`,
            },
            {
                accessorKey: 'status',
                meta: {
                    aiChat: { label: t('table_header__status', { ns: 'quotes-orders-list' }) },
                    sortBy: QuotesOrdersSortBy.STATUS,
                },
                header: t('table_header__status', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => <OrderStatusBadge status={row.original.status} type={row.original.type} />,
            },

            {
                accessorKey: 'clerk_id',
                meta: {
                    enabled: canReadClerkAssignment,
                    aiChat: {
                        description: 'ERP clerk recorded on the document, shown as id and name',
                        requires: ['canReadClerkAssignment'],
                    },
                },
                header: t('table_header__clerk', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) =>
                    row.original.clerk_id
                        ? row.original.clerk_id === row.original.clerk_name
                            ? row.original.clerk_id
                            : safeJoin([row.original.clerk_id, row.original.clerk_name], ': ')
                        : '-',
            },
            {
                accessorKey: 'po_number',
                meta: {
                    enabled: canReadPONumber,
                    aiChat: {
                        description: "The customer's purchase order number",
                        requires: ['canReadPONumber'],
                    },
                },
                header: t('table_header__po_number', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => row.original.po_number || '-',
            },
        ],
        [canReadClerkAssignment, canReadGrossMargin, canReadPONumber, t, trackMetricHintOpened],
    );

    return orderColumns;
}
